import { ActionTable, PermissionCheck } from '@/components';
import { useATSelection } from '@/components/table/hooks';
import { useAppSelector } from '@/state';
import { ProductionApi } from '@/state/api';
import { ProductionType } from '@/types';
import { permissionsKeys } from '@/types/permissionsType';
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  DataList,
  EmptyState,
  NumberInput,
  OverlayDialog,
  Table,
  toasterApi,
} from '@ui';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import z from 'zod';

type ShippingListFramesProps = {
  selectedShippingList:
    | ProductionType.ShippingList.GetShippingLists['response'][number]
    | null;
};

const ShippingListFrames_Inner = ({
  selectedShippingList,
}: ShippingListFramesProps) => {
  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );

  const selectedRelease = useAppSelector(
    (state) => state.release.selectedRelease,
  );

  // Edited frame quantity before adding as selected frame and quantity [frame_id -> changed quantity]
  const [editedFrameQuantities, setEditedFrameQuantities] = useState<
    Map<number, number>
  >(() => new Map());
  // Selected Frames and quantity
  const { selection } = useATSelection();
  const selectedShippingListFramesAndQuantity: ProductionType.ShippingList.GetShippingListFrames['response']['items'] =
    selection.getArray();

  // Delete from shipping list
  const [deleteShippingListFramesMutation] =
    ProductionApi.Api.useDeleteShippingListFramesMutation();

  // Get shipping list frames
  const {
    data: getShippingListFramesQuery,
    isLoading: isLoadingShippingListFrames,
  } = ProductionApi.Api.useGetShippingListFramesQuery(
    selectedShippingList?.id
      ? {
          params: {
            shipping_list_id: selectedShippingList?.id,
          },
        }
      : skipToken,
  );

  const handleDeleteFromPackingSlip = () => {
    const payload: ProductionType.ShippingList.DeleteShippingListFrames['body'] =
      selectedShippingListFramesAndQuantity
        ?.filter((row) => row?.allowed_to_delete > 0)
        ?.map((row) => {
          return {
            item_id: row?.id,
            quantity: editedFrameQuantities?.get(row?.frame_id) ?? 1,
          };
        });

    if (!payload?.length) return;

    OverlayDialog.open('deletePacking', {
      overlayId: 'deletePacking',
      title: 'Packing',
      description:
        'Are you sure you want to delete the frames from the packing list?',
      content: (
        <VStack align="flex-start" gap={2}>
          <Text color="tertiary.fg">Frame Orders:</Text>
          <DataList
            items={[
              {
                label: 'Total Frames to delete from Shipping List',
                value: `${selectedShippingListFramesAndQuantity?.length}`,
              },
            ]}
            orientation="horizontal"
            divideY="1px"
          />
          <Table
            columns={[
              {
                frameName: {
                  label: 'Frame',
                  width: '200px',
                },
              },
              {
                quantity: {
                  label: 'Quantity',
                  width: '100px',
                },
              },
              {
                packing_slip_name: {
                  label: 'Packing Slip',
                  width: '200px',
                },
              },
            ]}
            items={
              payload?.map((item) => ({
                frameName: selectedShippingListFramesAndQuantity?.find(
                  (frame) => frame?.id === item?.item_id,
                )?.frame_name,
                quantity: item?.quantity,
                packing_slip_name: selectedShippingListFramesAndQuantity?.find(
                  (frame) => frame?.id === item?.item_id,
                )?.packing_slip_name,
              })) ?? []
            }
          />
        </VStack>
      ),
      handleConfirm: () => {
        if (!selectedShippingList?.id) return;

        deleteShippingListFramesMutation({
          params: {
            shipping_list_id: selectedShippingList?.id,
          },
          body: payload,
        })
          .unwrap()
          .then(() => {
            toasterApi({
              type: 'success',
              status: 200,
              code: 'Success',
              message: 'Frames deleted from shipping list successfully',
            });

            // Reset all states
            selection.clear();
          })
          .catch(() => {
            toasterApi({
              type: 'error',
              status: 500,
              code: 'Error',
              message: 'Could not delete frames from shipping list',
            });
          })
          .finally(() => {
            // Reset all states
            OverlayDialog.close('deletePacking');
          });
      },
    });
  };

  // Reset edited quantities if frames list changes on invalidation
  useEffect(() => {
    setEditedFrameQuantities(new Map());
  }, [getShippingListFramesQuery]);

  const renderCell = (
    props: ActionTable.RenderCellType<ShippingListFramesType>,
  ) => {
    const { colKey, cellValue, rowValue } = props;

    switch (colKey) {
      case 'quantity': {
        const item = rowValue;
        return (
          <Box width="100px" gapX={2} onClick={(e) => e.stopPropagation()}>
            <NumberInput
              defaultValue={1}
              min={1}
              max={item?.allowed_to_delete}
              value={editedFrameQuantities?.get(item?.frame_id as number) ?? 1}
              onChange={(value: number) => {
                if (!getShippingListFramesQuery?.items?.length) return;

                // Update edited frame quantity before adding as selected frame and quantity
                setEditedFrameQuantities((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(item?.frame_id, value);
                  return newMap;
                });
              }}
            />
            <Text fontSize="xs" color="fg.warning">
              Available: {item?.allowed_to_delete}
            </Text>
          </Box>
        );
      }
      default:
        return cellValue?.value ?? cellValue ?? '';
    }
  };

  return (
    <Flex flexDirection="column" gapY={4} width="full">
      <Flex width="full" flexDirection="row" bg="white" color="black">
        <Flex
          justifyContent="space-between"
          width="full"
          alignItems="center"
          padding={4}
        >
          <Heading size="5xl" textTransform="uppercase">
            Packing
            <br />
            Slip
          </Heading>
          <Image
            src="/logo-karas.png"
            alt="Karas Logo"
            width="200px"
            height="100px"
            objectFit="contain"
          />
        </Flex>
      </Flex>

      {!selectedShippingList?.id ? (
        <EmptyState
          isEmpty
          position="relative"
          title="Select a shipping list"
          description="Please select a shipping list to view the packing slip."
        />
      ) : (
        <Flex flexDirection="column" gapY={4}>
          <Flex flexDirection="column" bg="white" color="black" p={4}>
            <Flex border="3px solid black" borderBottom="none" fontSize="sm">
              <Flex p={4} borderRight="2px solid black">
                <Image
                  src="/logo-karas.png"
                  alt="Karas logo"
                  height="40px"
                  width="100px"
                />
              </Flex>
              <Flex
                flexDirection="column"
                flex={1}
                borderRight="2px solid black"
              >
                <Flex
                  borderBottom="2px solid black"
                  flex={1}
                  alignItems="center"
                  paddingLeft={2}
                >
                  <Text fontWeight="semibold">Project Name:</Text>&nbsp;
                  {selectedProject.item?.name ?? 'N/A'}
                </Flex>
                <Flex flex={1} alignItems="center" paddingLeft={2}>
                  <Text fontWeight="semibold">Project Number:</Text>&nbsp;
                  {selectedProject.item?.id ?? 'N/A'}
                </Flex>
              </Flex>
              <Flex
                flexDirection="column"
                flex={1}
                borderRight="2px solid black"
              >
                <Flex
                  borderBottom="2px solid black"
                  flex={1}
                  alignItems="center"
                  paddingLeft={2}
                >
                  <Text fontWeight="semibold">Release #:</Text>&nbsp;
                  {selectedRelease.item?.id ?? 'N/A'}
                </Flex>
                <Flex flex={1} alignItems="center" paddingLeft={2}>
                  <Text fontWeight="semibold">Scope:</Text>&nbsp;
                </Flex>
              </Flex>
              <Flex flexDirection="column" flex={1}>
                <Flex
                  borderBottom="2px solid black"
                  flex={1}
                  alignItems="center"
                  paddingLeft={2}
                >
                  <Text fontWeight="semibold">Date:</Text>&nbsp;
                  {dayjs().format('MMM DD, YYYY')}
                </Flex>
                <Flex flex={1} alignItems="center" paddingLeft={2}>
                  <Text fontWeight="semibold">Revision:</Text>&nbsp;
                </Flex>
              </Flex>
            </Flex>
            <Flex border="3px solid black" p={2}>
              <Center width="full">
                <Text textTransform="uppercase" fontWeight="bold">
                  Frame Shipping List
                </Text>
              </Center>
            </Flex>
          </Flex>

          <Flex flexDirection="column" gapY={4}>
            <Flex flexDirection="column" gapY={2}>
              <>
                <ActionTable.FilterBar
                  disabled={!selectedRelease.id || !selectedShippingList?.id}
                  filterConfigComponents={filterConfigComponents}
                />
                <Box
                  key={`shipping-list-frames-table-${selectedProject?.id}`}
                  display="flex"
                  flexDirection="row"
                  gap={5}
                >
                  <Box h="100%" my={5}>
                    <ActionTable<ShippingListFramesType>
                      width="50vw"
                      columns={columns}
                      columnToUseAsRowKey="id"
                      items={getShippingListFramesQuery?.items ?? []}
                      isPaginated={false}
                      isPaginatedAsync={false}
                      totalItemsCount={
                        getShippingListFramesQuery?.items?.length ?? 0
                      }
                      isSortable={true}
                      isFilterable={true}
                      isShowActionBar={true}
                      height={'55vh'}
                      rowHeight={100}
                      renderCell={renderCell}
                      isLoading={
                        selectedProject.id ? isLoadingShippingListFrames : false
                      }
                      isRowSelectionDisabled={(row) =>
                        row?.allowed_to_delete <= 0
                      }
                      isMultipleSelection={true}
                      isCheckbox
                    />
                  </Box>
                </Box>
              </>
            </Flex>
            <Flex justifyContent="space-between">
              <PermissionCheck
                permissions={[permissionsKeys.delete_shippinglist]}
              >
                <Button
                  bg="red.700"
                  onClick={handleDeleteFromPackingSlip}
                  disabled={!selectedShippingListFramesAndQuantity?.length}
                >
                  Delete from Shipping
                </Button>
              </PermissionCheck>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const ShippingListFrames = ({
  selectedShippingList,
}: ShippingListFramesProps) => {
  return (
    <ActionTable.Provider>
      <ShippingListFrames_Inner selectedShippingList={selectedShippingList} />
    </ActionTable.Provider>
  );
};
export default ShippingListFrames;

const shippingListFramesTableSchema =
  ProductionType.ShippingList.getShippingListFrames.response.shape.items
    .element;

type ShippingListFramesType = z.infer<typeof shippingListFramesTableSchema>;

const columns: ActionTable.ColumnsConfigType<ShippingListFramesType> = [
  {
    frame_name: {
      label: 'Frame',
      width: '150px',
    },
  },
  {
    quantity: {
      label: 'Remaining Quantity',
      width: '200px',
    },
  },
  {
    packing_slip_name: {
      label: 'Packing Slip',
      width: '150px',
    },
  },
];

const filterConfigComponents: ActionTable.FilterConfigComponentType<ShippingListFramesType>[] =
  [
    {
      filterComponent: 'input',
      filterComponentLabel: 'Frame',
      filterConfigItems: [
        {
          colKeys: ['frame_name'],
          value: '',
          operator: 'contains',
        },
      ],
    },
    {
      filterComponent: 'input',
      filterComponentLabel: 'Packing Slip',
      filterConfigItems: [
        {
          colKeys: ['packing_slip_name'],
          value: '',
          operator: 'contains',
        },
      ],
    },
  ];
